import React, { CSSProperties } from 'react';
import { useFocusable, type UseFocusableOptions } from '@/hooks/useFocusable';
import styles from './FocusableButton.module.scss';
import { logger } from '@/utils/devUtils';
import { FocusableComponentLayout } from '@noriginmedia/norigin-spatial-navigation';

export interface FocusableButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  focusKey: string;
  children: React.ReactNode;
  onClick?: () => void;
  onFocusChange?: (element: HTMLElement | null, layout: FocusableComponentLayout) => void;
  onArrowPress?: UseFocusableOptions['onArrowPress'];
  onBackPress?: () => boolean | void; // 返回键回调，返回 true 表示已处理（阻止传播），false 或 void 表示未处理（继续传播）
  style?: CSSProperties;
  className?: string;
  focusedClassName?: string; // 自定义获焦样式类名
}

export const FocusableButton: React.FC<FocusableButtonProps> = ({
  focusKey,
  children,
  onClick,
  onFocusChange,
  onArrowPress,
  onBackPress,
  style,
  className = '',
  focusedClassName,
  ...rest
}) => {
  const { ref, focused } = useFocusable({
    focusKey,
    onEnterPress: onClick,
    onFocus: (layout) => {
      logger.debug(`[FocusableButton] ${focusKey} 获焦`);
      onFocusChange?.(ref.current, layout);
    },
    onArrowPress: (...args) => {
      // 如果没有提供 onArrowPress 回调，默认返回 true（允许导航）
      // 如果提供了回调，使用回调的返回值（false = 阻止导航，true = 允许导航）
      return onArrowPress ? onArrowPress(...args) : true;
    },
    onBackPress: onBackPress
      ? () => {
          logger.debug(`[FocusableButton] ${focusKey} 返回键被按下`);
          return onBackPress();
        }
      : undefined,
  });

  // 如果提供了自定义 focusedClassName，使用它；否则使用默认的 styles.focused
  const focusedClass = focusedClassName || styles.focused;

  return (
    <button
      ref={ref}
      className={`${styles.button} ${focused ? focusedClass : ''} ${className}`}
      style={style}
      onClick={onClick}
      {...rest}
    >
      {children}
    </button>
  );
};
